import { motion } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'

interface DiscussionProps {
  room: Room
  playerId: string
  send: SendFn
}

export default function Discussion({ room, playerId, send }: DiscussionProps) {
  const isRanker = playerId === room.currentRankerId
  const rankerName = room.currentRankerId ? room.players[room.currentRankerId]?.name : '???'

  const startRanking = () => {
    send('start_discussion_to_ranking', {})
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-lg mx-auto w-full">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', damping: 10 }}
        className="text-center mb-8"
      >
        <p className="font-body text-warmblack/50 text-base mb-2">Round {room.roundNumber}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-warmblack mb-2">
          {isRanker ? "You're the ranker!" : `${rankerName} is ranking`}
        </h2>
        <p className="font-body text-lg text-warmblack/60">
          {isRanker
            ? "Here are your 5 items. Discuss with the group, then rank them privately."
            : "Discuss these items — how would " + rankerName + " rank them?"
          }
        </p>
      </motion.div>

      {/* Show all 5 items */}
      <div className="w-full flex flex-col gap-3 mb-8">
        {room.drawnItems.map((item, i) => (
          <motion.div
            key={item}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: i * 0.15, type: 'spring', damping: 12 }}
            className="bg-white px-6 py-4 rounded-xl shadow-sm border border-warmblack/5 font-body text-xl text-warmblack text-center"
          >
            {item}
          </motion.div>
        ))}
      </div>

      {isRanker && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          whileTap={{ scale: 0.95 }}
          onClick={startRanking}
          className="w-full bg-coral hover:bg-coral/90 text-white font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all animate-pulse-glow"
        >
          I'm Ready to Rank
        </motion.button>
      )}

      {!isRanker && (
        <p className="text-warmblack/40 font-body text-base">
          Waiting for {rankerName} to finish ranking...
        </p>
      )}
    </div>
  )
}
